import { PageHead } from '../../components/ui/PageHead';
import { PageHero } from '../../components/hero/PageHero';
import { Section } from '../../components/layout/Section';
import { Container } from '../../components/layout/Container';
import { Grid } from '../../components/layout/Grid';
import { Stack } from '../../components/layout/Stack';
import { IndustryCard } from '../../components/cards/IndustryCard';
import { UseCaseCard } from '../../components/cards/UseCaseCard';
import { RegulatoryComplianceStrip } from '../../components/content/RegulatoryComplianceStrip';
import { CTABand } from '../../components/cta/CTABand';
import styles from './SolutionsPage.module.css';

const industries = [
  {
    icon: '🏦',
    industry: 'Financial Services',
    description:
      'Regulatory-compliant data platforms engineered for risk management, fraud detection, anti-money-laundering analytics, and real-time trading intelligence in complex multi-entity environments.',
    outcomes: [
      'SOX-compliant data lineage & audit trails',
      'Real-time fraud detection under 50ms',
      'Basel III & FINRA reporting automation',
      'Cross-entity risk aggregation',
    ],
  },
  {
    icon: '🏥',
    industry: 'Healthcare',
    description:
      'HIPAA-native Lakehouse architectures that unify EHR, claims, imaging, and IoT data - enabling clinical analytics, population health management, and AI-assisted care optimization.',
    outcomes: [
      'HIPAA-native access controls & audit',
      'EHR & HL7 FHIR integration patterns',
      'Population health AI models',
      'Clinical trial data lake management',
    ],
  },
  {
    icon: '🧬',
    industry: 'Pharmaceuticals',
    description:
      'GxP-validated data environments supporting compound discovery, clinical trial management, manufacturing quality systems, and accelerated regulatory submissions.',
    outcomes: [
      'FDA 21 CFR Part 11 compliance',
      'Clinical trial randomization & EDC lakes',
      'Manufacturing QC anomaly detection',
      'Regulatory submission data packages',
    ],
  },
  {
    icon: '🛢️',
    industry: 'Petroleum',
    description:
      'Integrated data platforms for upstream exploration, midstream logistics, and downstream refining - enabling real-time production analytics, asset integrity monitoring, and ESG compliance reporting.',
    outcomes: [
      'Real-time reservoir & production analytics',
      'Pipeline integrity & anomaly monitoring',
      'ESG emissions tracking & reporting',
      'HSE compliance & incident intelligence',
    ],
  },
];

const financialUseCases = [
  { tag: 'Risk', title: 'Credit risk model feature engineering at scale', description: 'Automated feature pipelines from 200+ risk signals for Basel-compliant credit models.' },
  { tag: 'Fraud', title: 'Real-time transaction anomaly detection', description: 'Sub-millisecond fraud scoring with Agentic AI monitoring and auto-blocking rules.' },
  { tag: 'Compliance', title: 'Automated regulatory reporting factory', description: 'FINRA, MiFID II, and Dodd-Frank reports auto-generated from Gold-layer datasets.' },
];

const healthcareUseCases = [
  { tag: 'Clinical', title: 'Unified patient timeline across 30+ data sources', description: 'EHR, claims, labs, and imaging stitched into a single longitudinal patient view.' },
  { tag: 'AI / ML', title: 'Sepsis risk prediction for ICU population', description: 'Real-time ML scoring on streaming vitals with 87% early-detection accuracy.' },
  { tag: 'Operations', title: 'Staff scheduling optimization from census forecasting', description: 'AI-driven demand forecasting reducing overtime costs by 23%.' },
];

const pharmaUseCases = [
  { tag: 'R&D', title: 'Multi-omics data lake for compound screening', description: 'Genomics, proteomics, and assay data unified for AI-assisted drug discovery.' },
  { tag: 'Clinical', title: 'Adaptive clinical trial data management', description: 'EDC, lab, and wearable data unified with automated safety signal detection.' },
  { tag: 'Manufacturing', title: 'Batch quality control and deviation tracking', description: 'AI-powered OOS detection reducing batch failures by 40%.' },
];

const petroleumUseCases = [
  { tag: 'Upstream', title: 'Real-time reservoir production optimization', description: 'Streaming sensor data from wellheads unified with geological models for AI-driven production tuning.' },
  { tag: 'Midstream', title: 'Pipeline integrity & leak detection', description: 'Continuous pressure and flow analytics with ML-based anomaly detection reducing incident response time by 60%.' },
  { tag: 'ESG', title: 'Emissions monitoring and regulatory reporting', description: 'Automated GHG emissions aggregation and SEC climate disclosure packages from unified operational data.' },
];

const financialFrameworks = ['SOX', 'Basel III', 'FINRA', 'MiFID II', 'Dodd-Frank', 'BCBS 239'];
const healthcareFrameworks = ['HIPAA', 'HL7 FHIR', 'HITRUST', '21st Century Cures Act'];
const pharmaFrameworks = ['FDA 21 CFR Part 11', 'GxP', 'ICH E6', 'EU GMP Annex 11', 'ISO 13485'];
const petroleumFrameworks = ['EPA Clean Air Act', 'SEC Climate Disclosure', 'OSHA PSM', 'API RP 1173', 'ISO 14001'];

export function SolutionsPage() {
  return (
    <>
      <PageHead
        title="Solutions"
        description="NAATS industry-specific Medallion Lakehouse solutions for Financial Services, Healthcare, and Pharmaceuticals - compliance-native, AI-ready."
      />

      <PageHero
        eyebrow="Industry Solutions"
        title="Compliance-Native Platforms for Regulated Industries"
        description="Platform capabilities mapped to the specific data, AI, and compliance requirements of Financial Services, Healthcare, and Pharmaceuticals."
      />

      {/* Industry Overview Cards */}
      <Section bg="page">
        <Container>
          <Grid cols={4} gap="md">
            {industries.map((ind) => (
              <IndustryCard key={ind.industry} {...ind} />
            ))}
          </Grid>
        </Container>
      </Section>

      {/* Financial Services deep-dive */}
      <Section bg="subtle" id="financial">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.industrySection}>
              <div className={styles.industryHeader}>
                <span className={styles.industryIcon}>🏦</span>
                <div>
                  <p className={styles.eyebrow}>Financial Services</p>
                  <h2 className={styles.industryTitle}>Risk, Compliance & Trading Intelligence</h2>
                </div>
              </div>
              <RegulatoryComplianceStrip frameworks={financialFrameworks} />
            </div>
            <Grid cols={3} gap="sm">
              {financialUseCases.map((uc) => (
                <UseCaseCard key={uc.title} {...uc} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Healthcare deep-dive */}
      <Section bg="page" id="healthcare">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.industrySection}>
              <div className={styles.industryHeader}>
                <span className={styles.industryIcon}>🏥</span>
                <div>
                  <p className={styles.eyebrow}>Healthcare</p>
                  <h2 className={styles.industryTitle}>Clinical Analytics & Population Health AI</h2>
                </div>
              </div>
              <RegulatoryComplianceStrip frameworks={healthcareFrameworks} />
            </div>
            <Grid cols={3} gap="sm">
              {healthcareUseCases.map((uc) => (
                <UseCaseCard key={uc.title} {...uc} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Pharma deep-dive */}
      <Section bg="subtle" id="pharma">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.industrySection}>
              <div className={styles.industryHeader}>
                <span className={styles.industryIcon}>🧬</span>
                <div>
                  <p className={styles.eyebrow}>Pharmaceuticals</p>
                  <h2 className={styles.industryTitle}>R&D Acceleration & Regulatory Compliance</h2>
                </div>
              </div>
              <RegulatoryComplianceStrip frameworks={pharmaFrameworks} />
            </div>
            <Grid cols={3} gap="sm">
              {pharmaUseCases.map((uc) => (
                <UseCaseCard key={uc.title} {...uc} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Petroleum deep-dive */}
      <Section bg="page" id="petroleum">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.industrySection}>
              <div className={styles.industryHeader}>
                <span className={styles.industryIcon}>🛢️</span>
                <div>
                  <p className={styles.eyebrow}>Petroleum</p>
                  <h2 className={styles.industryTitle}>Production Intelligence & ESG Compliance</h2>
                </div>
              </div>
              <RegulatoryComplianceStrip frameworks={petroleumFrameworks} />
            </div>
            <Grid cols={3} gap="sm">
              {petroleumUseCases.map((uc) => (
                <UseCaseCard key={uc.title} {...uc} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      <CTABand
        heading="Find Your Industry Solution"
        subheading="Speak with a NAATS expert to find the right solution for your business."
        primaryLabel="Talk to a Specialist"
        secondaryLabel="View the Platform"
        secondaryTo="/platform"
      />
    </>
  );
}
